// ── Geo Heatmap — severity-coloured dot-grid for spatial clustering ──

import { useMemo } from "react";
import { motion } from "framer-motion";
import type { GeoCellSummary } from "../types/graph";
import { severityColor, severityLabel } from "../utils/colors";

interface GeoHeatmapProps {
  cells: GeoCellSummary[];
  width?: number;
  height?: number;
}

/** Normalise lat/lon to [padding, size - padding] pixel range. */
function projectCells(
  cells: GeoCellSummary[],
  w: number,
  h: number,
  pad: number,
) {
  if (cells.length === 0) return [];

  const lats = cells.map((c) => c.lat);
  const lons = cells.map((c) => c.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latRange = maxLat - minLat || 0.001;
  const lonRange = maxLon - minLon || 0.001;

  return cells.map((c) => ({
    ...c,
    px: pad + ((c.lon - minLon) / lonRange) * (w - 2 * pad),
    // Flip Y — lat increases upward, pixels increase downward
    py: pad + ((maxLat - c.lat) / latRange) * (h - 2 * pad),
  }));
}

export default function GeoHeatmap({
  cells,
  width = 420,
  height = 300,
}: GeoHeatmapProps) {
  const projected = useMemo(
    () => projectCells(cells, width, height, 24),
    [cells, width, height],
  );

  if (projected.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-fl-evergreen-60 text-sm">
        No geospatial data for this campaign
      </div>
    );
  }

  const maxTotal = Math.max(...projected.map((c) => c.total));

  return (
    <div className="relative">
      {/* Title */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs uppercase tracking-widest text-fl-forest-60">
          Spatial Heatmap
        </span>
        <span className="text-xs text-fl-evergreen-60">
          ({cells.length} cells · ~11m grid)
        </span>
      </div>

      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="rounded-lg bg-fl-sand-20 border border-fl-evergreen-20"
      >
        {/* Pulse rings for high-severity clusters */}
        {projected
          .filter((c) => c.maxSeverity >= 4 && c.total >= 3)
          .map((c) => {
            const pulseR = maxTotal > 0 ? 8 + (c.total / maxTotal) * 18 : 8;
            const pulseEnd = maxTotal > 0 ? 20 + (c.total / maxTotal) * 18 : 20;
            return (
              <motion.circle
                key={`pulse-${c.cellId}`}
                cx={c.px}
                cy={c.py}
                fill="none"
                stroke={severityColor(c.maxSeverity)}
                strokeWidth={1}
                strokeOpacity={0.3}
                initial={{ r: pulseR, opacity: 0.4 }}
                animate={{ r: [pulseR, pulseEnd], opacity: [0.4, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
              />
            );
          })}

        {/* Cell dots */}
        {projected.map((c, i) => {
          const radius = maxTotal > 0 ? 4 + (c.total / maxTotal) * 14 : 4;
          const color = severityColor(c.avgSeverity);
          return (
            <motion.circle
              key={c.cellId}
              cx={c.px}
              cy={c.py}
              r={radius}
              fill={color}
              fillOpacity={0.7}
              stroke={c.maxSeverity >= 4 ? "#1f3a32" : color}
              strokeWidth={c.maxSeverity >= 4 ? 1.5 : 0.5}
              strokeOpacity={0.5}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: i * 0.02, duration: 0.4, type: "spring" }}
            >
              <title>
                {`${c.total} inspections · avg ${c.avgSeverity.toFixed(1)} (${severityLabel(c.avgSeverity)})${c.highCount > 0 ? ` · ${c.highCount} critical` : ""}`}
              </title>
            </motion.circle>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-xs text-fl-evergreen-60">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          Low
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-500" />
          High
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          Critical
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <span className="w-3 h-3 rounded-full border border-fl-evergreen-40 opacity-50" />
          = density
        </div>
      </div>
    </div>
  );
}
